import React, { useEffect, useState } from "react";
import { ChatAndCallPackageInterface } from "../../interfaces/chat-and-call-package.interface";
import { post, put } from "../../redux/ApiHelper";
import { successToast, errorToast } from "../../utils/toast.util";

interface ChatAndCallPackageModalProps {
  isOpen: boolean;
  closeModal: () => void;
  isEdit?: boolean;
  editData?: ChatAndCallPackageInterface | null;
  onSuccess?: () => void;
}

const ChatAndCallPackageModal: React.FC<ChatAndCallPackageModalProps> = ({
  isOpen,
  closeModal,
  isEdit = false,
  editData = null,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    balance: "",
    price: "",
  });
  const [errors, setErrors] = useState({
    balance: "",
    price: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit && editData) {
      setFormData({
        balance: editData.balance?.toString() || "",
        price: editData.price?.toString() || "",
      });
    } else {
      setFormData({
        balance: "",
        price: "",
      });
    }
    setErrors({ balance: "", price: "" });
  }, [isEdit, editData, isOpen]);

  const validateForm = (): boolean => {
    const newErrors = { balance: "", price: "" };
    let isValid = true;

    if (!formData.balance.trim()) {
      newErrors.balance = "Balance is required";
      isValid = false;
    } else if (parseFloat(formData.balance) <= 0) {
      newErrors.balance = "Balance must be greater than 0";
      isValid = false;
    }

    if (!formData.price.trim()) {
      newErrors.price = "Price is required";
      isValid = false;
    } else if (parseFloat(formData.price) <= 0) {
      newErrors.price = "Price must be greater than 0";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);

      const packageData = {
        balance: parseFloat(formData.balance),
        price: parseFloat(formData.price),
      };

      if (isEdit && editData?._id) {
        await put(
          `/admin/chat-call/chat-and-call-packages/${editData._id}`,
          packageData
        );
        successToast("Package updated successfully");
      } else {
        await post(
          `/admin/chat-call/chat-and-call-packages`,
          packageData
        );
        successToast("Package created successfully");
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || "Failed to save package. Please try again.";
      errorToast(errorMessage);
      console.error("Error saving package:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-800 w-full max-w-lg mx-4 p-6 rounded-lg shadow-lg max-h-screen overflow-auto border border-gray-700">
        <h2 className="text-xl font-semibold text-yellow-500 mb-4">
          {isEdit ? "Update" : "Create New"} Chat and Call Package
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-2 text-left">
              Balance <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="balance"
              value={formData.balance}
              onChange={handleChange}
              step="any"
              min="0"
              className={`w-full px-4 py-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 ${
                errors.balance
                  ? "focus:ring-red-500 border-red-500"
                  : "focus:ring-yellow-500"
              }`}
              placeholder="Enter balance"
            />
            {errors.balance && (
              <p className="text-red-500 text-sm mt-1 text-left">
                {errors.balance}
              </p>
            )}
          </div>

          <div>
            <label className="block text-gray-300 mb-2 text-left">
              Price (INR) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              step="any"
              min="0"
              className={`w-full px-4 py-2 rounded bg-gray-700 text-yellow-500 focus:outline-none focus:ring-2 ${
                errors.price
                  ? "focus:ring-red-500 border-red-500"
                  : "focus:ring-yellow-500"
              }`}
              placeholder="Enter price in INR"
            />
            {errors.price && (
              <p className="text-red-500 text-sm mt-1 text-left">
                {errors.price}
              </p>
            )}
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-600 text-gray-200 rounded-md hover:bg-gray-500 transition"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 rounded-md transition ${
                isSubmitting
                  ? "bg-yellow-600 opacity-50 cursor-not-allowed"
                  : "bg-yellow-500 text-gray-900 hover:bg-yellow-400"
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isEdit
                  ? "Updating..."
                  : "Creating..."
                : isEdit
                ? "Update Package"
                : "Create Package"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChatAndCallPackageModal;
